import { useState, useCallback, useEffect, useMemo } from 'react';

export const HORIZONTAL_GRID_GAME_CARD_SIZE = {
  DEFAULT: 'default',
  COMPACT: 'compact',
} as const;

export type HorizontalGridCardSize =
  (typeof HORIZONTAL_GRID_GAME_CARD_SIZE)[keyof typeof HORIZONTAL_GRID_GAME_CARD_SIZE];

interface HorizontalGridDimensions {
  itemWidth: number;
  listHeight: number;
  getGap: () => number;
  getPadding: () => { start: number; end: number };
}

/**
 * Height of one horizontal game row at the default card size.
 *
 * Exported as a pure function so that anything reserving space for a row before the row
 * exists - the lazy section placeholder - reads the same numbers the row will actually
 * render at, instead of keeping a second copy that drifts out of step with this one.
 */
export function getHorizontalRowHeight(viewportWidth: number): number {
  if (viewportWidth >= 1026) return 339;
  if (viewportWidth >= 501) return 226;
  if (viewportWidth >= 375) return Math.floor(viewportWidth * 0.342 * 1.378);
  return 182;
}

export function useHorizontalGridDimensions(
  hasJackpotPanel: boolean,
  gameCardSize: HorizontalGridCardSize = HORIZONTAL_GRID_GAME_CARD_SIZE.DEFAULT,
): HorizontalGridDimensions {
  const isCompactCardSize = gameCardSize === HORIZONTAL_GRID_GAME_CARD_SIZE.COMPACT;

  const getCompactDimension = useCallback((viewportWidth: number, minSize: number, maxSize: number) => {
    if (viewportWidth <= 375) return minSize;
    if (viewportWidth >= 440) return maxSize;

    const progress = (viewportWidth - 375) / 65; // 65 is the range between 375 and 440
    return Math.floor(minSize + (maxSize - minSize) * progress);
  }, []);

  const getGap = useCallback(() => {
    if (typeof window === 'undefined') return 8;
    if (isCompactCardSize) return 8;

    return window.innerWidth >= 1025 ? 10 : 8;
  }, [isCompactCardSize]);

  const getPadding = useCallback(() => {
    if (typeof window === 'undefined') return { start: 8, end: 8 };
    if (isCompactCardSize) return { start: 8, end: 8 };

    const padding = window.innerWidth >= 1025 ? 20 : 8;
    return { start: padding, end: padding };
  }, [isCompactCardSize]);

  const getItemWidth = useCallback(() => {
    if (typeof window === 'undefined') return 148;

    const viewportWidth = window.innerWidth;
    const gap = getGap();

    if (isCompactCardSize) return getCompactDimension(viewportWidth, 133, 147) + gap;

    let cardWidth = 132;
    if (viewportWidth >= 1026) cardWidth = 246;
    else if (viewportWidth >= 501) cardWidth = 164;
    else if (viewportWidth >= 375 && viewportWidth <= 500) cardWidth = Math.floor(viewportWidth * 0.342);
    return cardWidth + gap;
  }, [getCompactDimension, getGap, isCompactCardSize]);

  const getListHeight = useCallback(
    (viewportWidth: number) => {
      if (typeof window === 'undefined') return 226;

      if (isCompactCardSize) return getCompactDimension(viewportWidth, 183, 203);

      return getHorizontalRowHeight(viewportWidth);
    },
    [getCompactDimension, isCompactCardSize],
  );

  // Used only to trigger listHeight recalculation on resize
  const getJackpotPanelWidth = useCallback(() => {
    if (!hasJackpotPanel || typeof window === 'undefined') return 0;
    const viewportWidth = window.innerWidth;
    const isLandscape = window.innerWidth > window.innerHeight;
    if (isLandscape || viewportWidth > 1025) return viewportWidth > 1025 ? 425 : 352;
    return 0;
  }, [hasJackpotPanel]);

  const [itemWidth, setItemWidth] = useState(getItemWidth);
  const [containerWidth, setContainerWidth] = useState(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const updateDimensions = () => {
      setItemWidth(getItemWidth());
      setContainerWidth(window.innerWidth);
    };

    const debouncedResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDimensions, 150);
    };

    // A rotation can't wait out the debounce: the virtualizer sees the new scroller width at
    // once, and pairing it with the old card width over-counts the cards in view.
    const portraitQuery = window.matchMedia('(orientation: portrait)');
    const onOrientationChange = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      updateDimensions();
    };

    updateDimensions();
    window.addEventListener('resize', debouncedResize);
    portraitQuery.addEventListener('change', onOrientationChange);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      portraitQuery.removeEventListener('change', onOrientationChange);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [getItemWidth, getJackpotPanelWidth]);

  // containerWidth is tracked in state so listHeight recalculates on resize
  const listHeight = useMemo(() => getListHeight(containerWidth), [getListHeight, containerWidth]);

  return { itemWidth, listHeight, getGap, getPadding };
}
